/**
 * The profile as optional sections. (Citizen first run · slice F)
 *
 * The old wizard's steps, each now a section of Settings → Profile that saves
 * on its own and gates nothing: Identity · Mobility & passport · Skills ·
 * Education · Experience. Import (CV / LinkedIn) and the identity document
 * (passport / ID) are actions that fill several at once. How complete each
 * section is comes from the server (`strength`); what here is pure is which
 * fields a section owns and what it sends.
 */
import { PROFILE_STRENGTH_SECTIONS, type ProfileStrengthSectionId } from '@tonnie/types';

import type { OnboardingData } from '../types';

/**
 * Where the Identity section says the citizen lives. The address it edits
 * wins: picking an address in another country moves the residence with it.
 * Without an address, the country setup recorded.
 */
function identityCountry(data: OnboardingData): string | undefined {
  return data.address?.countryCode || data.currentCountry || undefined;
}

/** The sections, in page order — the server's list, so the two agree. */
export const PROFILE_SECTIONS = PROFILE_STRENGTH_SECTIONS;

export type ProfileSectionId = ProfileStrengthSectionId;

/**
 * Where a link into the profile can point: a section, the CV / LinkedIn
 * import, or the passport / ID upload.
 */
export type ProfileSectionTarget = ProfileSectionId | 'import' | 'document';

/** The legacy wizard step each section's form (and validator) comes from. */
export const SECTION_STEP: Record<ProfileSectionId, string> = {
  identity: 'personal',
  mobility: 'mobility',
  skills: 'skills',
  education: 'education',
  experience: 'experience',
};

export function isProfileSectionTarget(value: string | null): value is ProfileSectionTarget {
  return (
    value === 'import' ||
    value === 'document' ||
    (PROFILE_SECTIONS as readonly string[]).includes(value ?? '')
  );
}

/**
 * The section an old `/onboarding?edit=true&step=…` link meant. The import
 * step is the Import section; a step that no longer exists opens the profile.
 */
export function sectionForLegacyStep(step: string | null): ProfileSectionTarget | null {
  if (step === 'linkedin-pdf') return 'import';
  const match = PROFILE_SECTIONS.find((section) => SECTION_STEP[section] === step);
  return match ?? null;
}

export function profileSectionHref(target?: ProfileSectionTarget | null): string {
  return target ? `/settings?tab=profile&section=${target}` : '/settings?tab=profile';
}

const filled = (value: string | undefined | null) =>
  typeof value === 'string' && value.trim().length > 0;

// ── Saving ────────────────────────────────────────────────────────────

/**
 * What saving a section sends — its own fields and nothing else, so a save
 * can never overwrite another section. Lists go as they are, empty included:
 * removing the last education entry is a change the server must receive.
 * Scalars that are empty are left out, because the server ignores an empty
 * scalar rather than clearing it (the section validator refuses to blank a
 * value on file for the same reason).
 */
export function sectionPayload(
  section: ProfileSectionId,
  data: OnboardingData
): Record<string, unknown> {
  switch (section) {
    case 'identity': {
      const payload: Record<string, unknown> = {};
      if (filled(data.firstName)) payload.firstName = data.firstName;
      if (filled(data.lastName)) payload.lastName = data.lastName;
      if (filled(data.dateOfBirth)) payload.dateOfBirth = data.dateOfBirth;
      if (filled(data.phone)) payload.phone = data.phone;
      if (filled(data.phoneCountryCode)) payload.phoneCountryCode = data.phoneCountryCode;
      if (data.address) payload.address = data.address;
      const country = identityCountry(data);
      if (country) payload.currentCountry = country;
      if (filled(data.avatarUrl)) payload.avatarUrl = data.avatarUrl;
      payload.websiteUrls = data.websiteUrls ?? [];
      return payload;
    }
    case 'mobility': {
      const payload: Record<string, unknown> = {
        familyConfig: data.familyConfig,
        workModelPreference: data.workModelPreference ?? [],
      };
      if (data.mobility) payload.mobility = data.mobility;
      // Citizenship is `nationalities` — the canonical list, never `mobility`.
      if (data.nationalities.length > 0) payload.nationalities = data.nationalities;
      return payload;
    }
    case 'skills':
      return data.skills ? { skills: data.skills } : {};
    case 'education':
      return { education: data.education ?? [] };
    case 'experience':
      return { experience: data.experience ?? [] };
    default: {
      const exhaustive: never = section;
      return exhaustive;
    }
  }
}
